import React, { forwardRef, useEffect, useImperativeHandle, useState, useMemo } from "react";
import classNames from "classnames";
import { useUpdateDeal } from "api/deals/service";
import { useTranslation } from "react-i18next";
import {
  FormGroup,
  Label,
  Input,
  Row,
  Col
} from "reactstrap";
import ReactDatetime from "components/CustomDatePicker";
import { formatPriceWithDollar } from "utils/currency";
import { formatDateForAPI } from "utils/dateUtils";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { useGetChannels } from "api/channels/service";
import moment from "moment";
import Select from "components/Select";
import ChannelsEmbedded from "views/settings/Channels/ChannelsEmbedded";

const DealInsuranceTab = forwardRef(({ data, id, activeColor = "blue", isTheme2 = false }, ref) => {
  const { t } = useTranslation();
  const updateDealMutation = useUpdateDeal();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".deal-insurance-tab.deal-insurance-tab--theme2",
            fieldsSelector: ".deal-insurance-tab-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle14 = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { borderRadius: 0, fontSize: "14px" };
  const dateInputStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { borderRadius: 0 };
  const [companySearch, setCompanySearch] = useState("");
  const [agentSearch, setAgentSearch] = useState("");

  const { data: companies, isLoading: isCompaniesLoading } = useGetChannels("insurance_company", 1, 100, companySearch);
  const { data: agents, isLoading: isAgentsLoading } = useGetChannels("insurance_agent", 1, 100, agentSearch);
  

  const [formData, setFormData] = useState({
    company: null,
    agent: null,
    policy_number: "",
    effective_date: null,
    collision_deduct: 0,
    expire_date: null,
    comprehensive_deduct: 0,
  });
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showAddAgent, setShowAddAgent] = useState(false);

  useEffect(() => {
    if (!data) return;
    setFormData(prev => ({
      company: data?.company?.id || null,
      agent: data?.agent?.id || null,
      policy_number: data.policy_number || "",
      effective_date: data.effective_date
        ? moment(data.effective_date).format("YYYY-MM-DD")
        : null,
      collision_deduct: data.collision_deduct ? formatPriceWithDollar(data.collision_deduct) : 0,
      expire_date: data.expire_date
        ? moment(data.expire_date).format("YYYY-MM-DD")
        : null,
      comprehensive_deduct: data.comprehensive_deduct ? formatPriceWithDollar(data.comprehensive_deduct) : 0,
    }));
  }, [data]);


  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  console.log("formData", formData);

  const handleSave = async () => {

    const updatePayload = {
      insurance: {
        company: formData.company,
        agent: formData.agent,
        policy_number: formData.policy_number || "",
        effective_date: formData.effective_date || null,
        // Store numeric values to backend (strip formatting)
        collision_deduct: formData.collision_deduct
          ? Number(String(formData.collision_deduct).replace(/[^\d.\-]/g, ""))
          : null,
        expire_date: formatDateForAPI(formData.expire_date),
        comprehensive_deduct: formData.comprehensive_deduct
          ? Number(String(formData.comprehensive_deduct).replace(/[^\d.\-]/g, ""))
          : null,
      },
    };

    console.log("updatePayload", updatePayload);

    try {
      await updateDealMutation.mutateAsync({
        id: id,
        data: updatePayload,
      });
      return { success: true };
    } catch (error) {
      // Error notifications handled in hook
      return { success: false };
    }
  };

  useImperativeHandle(ref, () => ({
    handleSave,
  }));

  return (
    <div
      className={classNames("row deal-insurance-tab", {
        "deal-insurance-tab--theme2": isTheme2,
      })}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <div className="col-md-12 mb-4">
        {!showAddCompany && !showAddAgent &&
          <div className="card rounded-0">
            <div className="card-header font-weight-bold">{t('dealInsuranceTab.insurance')}</div>
            <div className={classNames("card-body", { "deal-insurance-tab-fields": isTheme2 })}>
              {/* Top row: 3 side-by-side */}
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label>{t('dealInsuranceTab.insuranceCompany')}</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder={t('dealInsuranceTab.selectCompany')}
                      name="company"
                      isSearchable
                      isLoading={isCompaniesLoading}
                      closeMenuOnSelect={true}
                      value={formData.company ? {
                        value: formData.company,
                        label: companies?.results?.find(company => String(company.id) === String(formData.company))?.legal_name || 
                               companies?.results?.find(company => String(company.id) === String(formData.company))?.name || 
                               formData.company
                      } : null}
                      onChange={(option) => {
                        if (option?.value === "__add_new_company__") {
                          setShowAddCompany(true);
                          return;
                        }
                        handleChange("company", option?.value || '');
                      }}
                      onInputChange={(newValue) => setCompanySearch(newValue)}
                      options={[
                        { value: '', label: t('dealInsuranceTab.selectCompany') },
                        { value: "__add_new_company__", label: t('dealInsuranceTab.addNew') },
                        ...(companies?.results?.map((company) => ({
                          value: String(company.id),
                          label: company.legal_name || company.name
                        })) || [])
                      ]}
                      isClearable
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>{t('dealInsuranceTab.insuranceAgent')}</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder={t('dealInsuranceTab.selectAgent')}
                      name="agent"
                      isSearchable
                      isLoading={isAgentsLoading}
                      closeMenuOnSelect={true}
                      value={formData.agent ? {
                        value: formData.agent,
                        label: agents?.results?.find(agent => String(agent.id) === String(formData.agent))?.legal_name || 
                               agents?.results?.find(agent => String(agent.id) === String(formData.agent))?.name || 
                               formData.agent
                      } : null}
                      onChange={(option) => {
                        if (option?.value === "__add_new_agent__") {
                          setShowAddAgent(true);
                          return;
                        }
                        handleChange("agent", option?.value || '');
                      }}
                      onInputChange={(newValue) => setAgentSearch(newValue)}
                      options={[
                        { value: '', label: t('dealInsuranceTab.selectAgent') },
                        { value: "__add_new_agent__", label: t('dealInsuranceTab.addNew') },
                        ...(agents?.results?.map((agent) => ({
                          value: String(agent.id),
                          label: agent.legal_name || agent.name
                        })) || [])
                      ]}
                      isClearable
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>{t('dealInsuranceTab.policyNumber')}</Label>
                    <Input
                      className="form-control"
                      style={inputStyle14}
                      type="text"
                      value={formData.policy_number}
                      onChange={(e) => handleChange("policy_number", e.target.value)}
                      placeholder={t('dealInsuranceTab.enterPolicyNumber')}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Bottom rows: 2 per row */}
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>{t('dealInsuranceTab.effectiveDate')}</Label>
                    <ReactDatetime
                      inputProps={{
                        className: "form-control",
                        placeholder: t('dealInsuranceTab.selectDate'),
                        style: dateInputStyle
                      }}
                      value={formData.effective_date ? moment(formData.effective_date, "YYYY-MM-DD") : null}
                      onChange={(date) =>
                        handleChange(
                          "effective_date",
                          date && moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : null
                        )
                      }
                      dateFormat="MM-DD-YYYY"
                      timeFormat={false}
                      closeOnSelect={true}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>{t('dealInsuranceTab.collisionDeduct')}</Label>
                    <Input
                      className="form-control"
                      style={inputStyle14}
                      type="text"
                      value={formData.collision_deduct}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d.]/g, "");
                        handleChange("collision_deduct", raw);
                      }}
                      onBlur={() => {
                        handleChange(
                          "collision_deduct",
                          formatPriceWithDollar(formData.collision_deduct)
                        );
                      }}
                      onFocus={() => {
                        const raw = String(formData.collision_deduct || "").replace(/[^\d.]/g, "");
                        handleChange("collision_deduct", raw);
                      }}
                      placeholder="$0.00"
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>{t('dealInsuranceTab.expireDate')}</Label>
                    <ReactDatetime
                      inputProps={{
                        className: "form-control",
                        placeholder: t('dealInsuranceTab.selectDate'),
                        style: dateInputStyle
                      }}
                      value={formData.expire_date ? moment(formData.expire_date, "YYYY-MM-DD") : null}
                      onChange={(date) =>
                        handleChange(
                          "expire_date",
                          date && moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : null
                        )
                      }
                      dateFormat="MM-DD-YYYY"
                      timeFormat={false}
                      closeOnSelect={true}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>{t('dealInsuranceTab.comprehensiveDeduct')}</Label>
                    <Input
                      className="form-control"
                      style={inputStyle14}
                      type="text"
                      value={formData.comprehensive_deduct}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d.]/g, "");
                        handleChange("comprehensive_deduct", raw);
                      }}
                      onBlur={() => {
                        handleChange(
                          "comprehensive_deduct",
                          formatPriceWithDollar(formData.comprehensive_deduct)
                        );
                      }}
                      onFocus={() => {
                        const raw = String(formData.comprehensive_deduct || "").replace(/[^\d.]/g, "");
                        handleChange("comprehensive_deduct", raw);
                      }}
                      placeholder="$0.00"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </div>
        }
        {showAddCompany && (
              <ChannelsEmbedded
              activeColor={activeColor}
                defaultType="insurance_company"
                onClose={() => setShowAddCompany(false)}
                onCreated={(created) => {
                  if (created?.id) {
                    handleChange("insurance_company", String(created.id));
                  } else if (created?.name) {
                    handleChange("insurance_company", created.name);
                  }
                }}
              />
        )}
        {showAddAgent && (
          <ChannelsEmbedded
            activeColor={activeColor}
            defaultType="insurance_agent"
            onClose={() => setShowAddAgent(false)}
            onCreated={(created) => {
              if (created?.id) {
                handleChange("insurance_agent", String(created.id));
              } else if (created?.name) {
                handleChange("insurance_agent", created.name);
              }
            }}
          />
        )}
      </div>
    </div>
  );
});

export default DealInsuranceTab;


